/**
 * Final-exam page — Sprint 4 / FR-F.
 *
 * One-item-at-a-time pagination (50 items would overwhelm a single
 * scroll view) with a persistent timer + answered-count meter at the
 * top. On submit, the page transitions to a "review" state that
 * mirrors the ModuleQuiz pass/fail UI but with per-domain breakdowns.
 *
 * DEC-0017 pause-time: the page instruments ``visibilitychange`` +
 * ``blur`` events to accumulate seconds where the tab was hidden /
 * the window unfocused. The accumulated count ships on the submit
 * payload. Phase 0: log only, no enforcement.
 *
 * Cooldown / gate errors surface via the typed errors in
 * ``exam-api.ts``; the page renders a banner for each kind instead
 * of showing the quiz.
 */
import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { API_BASE_URL, ApiError, refreshSession } from "@/lib/api";
import {
  ExamCooldownError,
  ExamModuleGateError,
  recordExamAnswer,
  startExam,
  submitExam,
  type StartExamResponse,
  type SubmitExamResponse,
} from "@/lib/exam-api";
import type { SubmitReviewItem } from "@/lib/lesson-api";

type ExamState =
  | { kind: "idle"; error: string | null }
  | { kind: "starting" }
  | {
      kind: "cooldown";
      until: Date;
      consecutive: number;
      cap: number;
    }
  | { kind: "gate"; missing: ExamModuleGateError["missing_modules"] }
  | {
      kind: "attempting";
      start: StartExamResponse;
      answers: Record<string, string>;
      currentIndex: number;
      submitting: boolean;
      error: string | null;
    }
  | { kind: "reviewing"; response: SubmitExamResponse };

function redirectToLogin(returnTo: string): void {
  const encoded = encodeURIComponent(returnTo);
  window.location.href = `${API_BASE_URL}/api/auth/login?return_to=${encoded}`;
}

function fmtCountdown(target: Date): string {
  const ms = target.getTime() - Date.now();
  if (ms <= 0) return "Ready";
  const totalMin = Math.ceil(ms / 60000);
  const hours = Math.floor(totalMin / 60);
  const minutes = totalMin % 60;
  return hours > 0
    ? `${hours}h ${minutes.toString().padStart(2, "0")}m`
    : `${minutes}m`;
}

function fmtElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export interface ExamPageProps {
  onUnauthenticated?: (returnTo: string) => void;
}

export function ExamPage({
  onUnauthenticated = redirectToLogin,
}: ExamPageProps = {}): React.JSX.Element {
  const { courseSlug = "cait-foundations" } = useParams<{ courseSlug: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<ExamState>({ kind: "idle", error: null });

  // Tick state for the countdown banner / timer.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (state.kind !== "cooldown" && state.kind !== "attempting") return undefined;
    const id = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [state.kind]);

  // DEC-0017 pause-time accumulator. Refs so visibilitychange + blur
  // handlers can mutate without re-renders.
  const pauseSecondsRef = useRef(0);
  const pauseStartRef = useRef<number | null>(null);

  useEffect(() => {
    if (state.kind !== "attempting") return undefined;
    const onHide = (): void => {
      if (pauseStartRef.current === null) {
        pauseStartRef.current = Date.now();
      }
    };
    const onShow = (): void => {
      if (pauseStartRef.current !== null) {
        const delta = Math.floor((Date.now() - pauseStartRef.current) / 1000);
        pauseSecondsRef.current += Math.max(0, delta);
        pauseStartRef.current = null;
      }
    };
    const onVisibility = (): void => {
      if (document.hidden) onHide();
      else onShow();
    };
    window.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", onHide);
    window.addEventListener("focus", onShow);
    return () => {
      window.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", onHide);
      window.removeEventListener("focus", onShow);
      // Final flush if a pause is still open when leaving the page.
      onShow();
    };
  }, [state.kind]);

  // Wall-clock elapsed seconds, used by the timer chip during attempting.
  const startedAtMs = useMemo(
    () => (state.kind === "attempting" ? Date.now() : 0),
    [state.kind === "attempting"], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const onStart = useCallback(async (): Promise<void> => {
    setState({ kind: "starting" });
    try {
      const r = await startExam(courseSlug);
      pauseSecondsRef.current = 0;
      pauseStartRef.current = null;
      setState({
        kind: "attempting",
        start: r,
        answers: {},
        currentIndex: 0,
        submitting: false,
        error: null,
      });
    } catch (err) {
      if (err instanceof ExamCooldownError) {
        setState({
          kind: "cooldown",
          until: err.cooldown_until,
          consecutive: err.consecutive_failed_attempts,
          cap: err.failure_cap,
        });
        return;
      }
      if (err instanceof ExamModuleGateError) {
        setState({ kind: "gate", missing: err.missing_modules });
        return;
      }
      if (err instanceof ApiError && err.status === 401) {
        onUnauthenticated(`/exam/${courseSlug}`);
        return;
      }
      const msg = err instanceof Error ? err.message : "Could not start.";
      setState({ kind: "idle", error: msg });
    }
  }, [courseSlug, onUnauthenticated]);

  const onAnswer = useCallback(
    async (itemId: string, optionId: string): Promise<void> => {
      // Read attemptId from the closure (not from inside setState updater).
      // React 18's automatic batching means the updater callback runs in a
      // later microtask, so a side-effect like attemptId = prev.start.attempt_id
      // inside the updater hasn't happened yet by the time the next line
      // of this function runs. Same fix as ModuleQuiz.tsx (commit 4246b53).
      const attemptId =
        state.kind === "attempting" ? state.start.attempt_id : "";
      setState((prev) => {
        if (prev.kind !== "attempting") return prev;
        return { ...prev, answers: { ...prev.answers, [itemId]: optionId } };
      });
      if (!attemptId) return;
      try {
        await recordExamAnswer(attemptId, itemId, optionId);
      } catch {
        /* fire-and-forget; last-write-wins on submit */
      }
    },
    [state],
  );

  const onSubmit = useCallback(async (): Promise<void> => {
    if (state.kind !== "attempting") return;
    setState({ ...state, submitting: true });
    // Flush any open pause window.
    if (pauseStartRef.current !== null) {
      pauseSecondsRef.current += Math.floor(
        (Date.now() - pauseStartRef.current) / 1000,
      );
      pauseStartRef.current = null;
    }
    // The full client answer map rides on the submit payload so a dropped
    // per-answer autosave (flaky connection) doesn't cost a real answer.
    const doSubmit = (): Promise<SubmitExamResponse> =>
      submitExam(state.start.attempt_id, pauseSecondsRef.current, state.answers);
    try {
      let r: SubmitExamResponse;
      try {
        r = await doSubmit();
      } catch (err) {
        // The exam can run up to 3 hours; the sealed session's access token
        // can expire mid-attempt and 401 the submit. Silently refresh the
        // session and retry once before giving up — answers ride on the
        // payload, so a successful refresh+retry loses nothing.
        if (err instanceof ApiError && err.status === 401) {
          if (await refreshSession()) {
            r = await doSubmit();
          } else {
            // Refresh token itself is dead → full re-auth. Answers that
            // reached the server during the attempt persist on the in-flight
            // attempt; returning lets the candidate resume and resubmit.
            onUnauthenticated(`/exam/${courseSlug}`);
            return;
          }
        } else {
          throw err;
        }
      }
      setState({ kind: "reviewing", response: r });
      if (r.passed) {
        // Course-complete celebration is a separate route; jump after a
        // moment so the user sees the pass banner first.
        window.setTimeout(() => {
          void navigate(`/course/${courseSlug}/complete`);
        }, 2500);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Could not submit.";
      setState({ ...state, submitting: false, error: msg });
    }
  }, [state, navigate, courseSlug, onUnauthenticated]);

  // ----- Render -----

  if (state.kind === "idle" || state.kind === "starting") {
    return (
      <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        <header>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Foundations Certificate · Final Exam
          </p>
          <h1 className="mt-1 text-3xl font-bold text-foreground">
            Final Exam
          </h1>
        </header>
        <article className="mt-8 rounded-lg border border-border bg-white p-6">
          <p className="text-sm text-foreground">
            50 multiple-choice questions drawn from the full course item bank
            with learning-objective + Bloom-level coverage. You'll have 3
            hours; pause time is logged but not enforced. You need 80%
            overall and 60% per competency domain to pass.
          </p>
          <p className="mt-3 text-sm text-foreground">
            Three failed attempts in a row trigger a 24-hour cooldown.
          </p>
          {state.kind === "idle" && state.error !== null ? (
            <p className="mt-3 text-sm text-red-700" role="alert">
              {state.error}
            </p>
          ) : null}
          <button
            type="button"
            onClick={() => {
              void onStart();
            }}
            disabled={state.kind === "starting"}
            className="mt-5 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            {state.kind === "starting" ? "Starting…" : "Start final exam"}
          </button>
        </article>
      </section>
    );
  }

  if (state.kind === "cooldown") {
    return (
      <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        <article
          className="rounded-lg border border-amber-300 bg-amber-50 p-6"
          data-testid="exam-cooldown-banner"
        >
          <h2 className="text-lg font-semibold text-foreground">
            Final exam cooldown
          </h2>
          <p className="mt-2 text-sm text-foreground">
            You've used all {state.cap} attempts in this cycle. Take time
            to review the course material — the exam unlocks again in{" "}
            <span className="font-mono font-semibold">
              {fmtCountdown(state.until)}
            </span>
            .
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            Unlocks at {state.until.toLocaleString()}.
          </p>
        </article>
      </section>
    );
  }

  if (state.kind === "gate") {
    return (
      <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        <article className="rounded-lg border border-amber-200 bg-white p-6">
          <h2 className="text-lg font-semibold text-foreground">
            One step left first
          </h2>
          <p className="mt-2 text-sm text-foreground">
            The final exam unlocks once you've passed every module quiz.{" "}
            {state.missing.length} module
            {state.missing.length === 1 ? "" : "s"} still pending:
          </p>
          <ul className="mt-4 space-y-2">
            {state.missing.map((m) => (
              <li key={m.id}>
                <Link
                  to={`/module/${m.slug}/quiz`}
                  className="font-semibold text-foreground underline hover:text-foreground"
                >
                  Module {m.sequence}: {m.title} →
                </Link>
              </li>
            ))}
          </ul>
        </article>
      </section>
    );
  }

  if (state.kind === "attempting") {
    const item = state.start.items[state.currentIndex];
    if (!item) {
      return <p className="p-8">Loading…</p>;
    }
    const allAnswered = state.start.items.every((i) =>
      Object.hasOwn(state.answers, i.id),
    );
    const elapsedSec = Math.floor((Date.now() - startedAtMs) / 1000);
    return (
      <section className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        <header className="flex flex-wrap items-baseline justify-between gap-3 border-b border-border pb-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Final Exam · Attempt {state.start.attempt_number}
            </p>
            <h1 className="text-2xl font-bold text-foreground">
              Question {state.currentIndex + 1} of {state.start.items.length}
            </h1>
          </div>
          <div className="flex gap-4 text-sm text-foreground">
            <span>
              Answered:{" "}
              <span className="font-semibold">
                {Object.keys(state.answers).length}/{state.start.items.length}
              </span>
            </span>
            <span data-testid="exam-timer">
              Elapsed:{" "}
              <span className="font-mono font-semibold">
                {fmtElapsed(elapsedSec)}
              </span>
            </span>
          </div>
        </header>

        <article className="mt-6 rounded-lg border border-border bg-white p-6">
          <p className="text-sm font-semibold text-foreground">{item.stem}</p>
          <fieldset className="mt-4 space-y-2">
            <legend className="sr-only">
              Options for question {state.currentIndex + 1}
            </legend>
            {item.options.map((opt) => {
              const selected = state.answers[item.id] === opt.id;
              return (
                <label
                  key={opt.id}
                  className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 text-sm ${
                    selected
                      ? "border-primary bg-background"
                      : "border-border hover:bg-muted"
                  }`}
                >
                  <input
                    type="radio"
                    name={item.id}
                    value={opt.id}
                    checked={selected}
                    onChange={() => {
                      void onAnswer(item.id, opt.id);
                    }}
                    className="mt-0.5"
                  />
                  <span className="flex-1">
                    <span className="font-mono text-xs uppercase text-muted-foreground">
                      {opt.id}.
                    </span>{" "}
                    {opt.text}
                  </span>
                </label>
              );
            })}
          </fieldset>
        </article>

        <nav className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            disabled={state.currentIndex === 0}
            onClick={() =>
              setState((prev) =>
                prev.kind === "attempting"
                  ? { ...prev, currentIndex: prev.currentIndex - 1 }
                  : prev,
              )
            }
            className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            ← Previous
          </button>
          {state.currentIndex < state.start.items.length - 1 ? (
            <button
              type="button"
              onClick={() =>
                setState((prev) =>
                  prev.kind === "attempting"
                    ? { ...prev, currentIndex: prev.currentIndex + 1 }
                    : prev,
                )
              }
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              Next →
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                void onSubmit();
              }}
              disabled={!allAnswered || state.submitting}
              className="inline-flex items-center gap-2 rounded-md bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-emerald-700 focus:ring-offset-2"
            >
              {state.submitting
                ? "Submitting…"
                : allAnswered
                  ? "Submit final exam"
                  : `Answer all ${state.start.items.length} questions first`}
            </button>
          )}
        </nav>
        {state.error !== null ? (
          <p className="mt-3 text-sm text-red-700" role="alert">
            {state.error}
          </p>
        ) : null}
      </section>
    );
  }

  // state.kind === "reviewing"
  const { response } = state;
  return (
    <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
      <article
        className={`rounded-lg border p-6 ${
          response.passed
            ? "border-emerald-200 bg-emerald-50"
            : "border-amber-200 bg-amber-50"
        }`}
        data-testid="exam-review"
      >
        <h2 className="text-lg font-semibold text-foreground">
          {response.passed
            ? `Passed — ${response.score}%`
            : `Not quite — ${response.score}%`}
        </h2>
        <p className="mt-2 text-sm text-foreground">
          Threshold: {response.passing_percent}% overall. Domain floor:
          60% per competency. Per-domain breakdown below.
        </p>
        <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {Object.entries(response.domain_scores).map(([domain, score]) => {
            const floor = response.domain_floor[domain] ?? 0;
            const met = score >= floor;
            return (
              <div
                key={domain}
                className={`rounded-md border p-3 text-sm ${
                  met ? "border-border bg-white" : "border-red-200 bg-red-50"
                }`}
              >
                <dt className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {domain}
                </dt>
                <dd className="mt-1 font-mono text-lg font-semibold text-foreground">
                  {score}%
                </dd>
                <dd className="text-xs text-muted-foreground">floor: {floor}%</dd>
              </div>
            );
          })}
        </dl>
        {response.passed ? (
          <p className="mt-6 text-sm text-emerald-900">
            You'll be redirected to your course-completion page in a moment.
          </p>
        ) : response.cooldown_until !== null ? (
          <p className="mt-6 text-sm text-amber-900">
            That was your 3rd consecutive failure. A 24-hour cooldown is
            active until{" "}
            <span className="font-mono">
              {new Date(response.cooldown_until).toLocaleString()}
            </span>
            .
          </p>
        ) : (
          <p className="mt-6 text-sm text-foreground">
            Take some time to review. You can retake whenever you're ready.
          </p>
        )}
      </article>

      <details className="mt-8 rounded-lg border border-border bg-white p-4">
        <summary className="cursor-pointer text-sm font-semibold text-foreground">
          Review all 50 questions
        </summary>
        <ol className="mt-4 space-y-4">
          {response.items.map((item: SubmitReviewItem, idx: number) => (
            <li key={item.id} className="rounded-md border border-border p-3">
              <p className="text-sm font-semibold text-foreground">
                {idx + 1}. {item.stem}
              </p>
              <ul className="mt-2 space-y-1 text-sm">
                {item.options.map((opt) => {
                  const isCandidate = opt.id === item.candidate_option_id;
                  const isCorrect = opt.id === item.correct_option_id;
                  return (
                    <li
                      key={opt.id}
                      className={`rounded px-2 py-1 ${
                        isCorrect
                          ? "bg-emerald-100 font-semibold text-emerald-900"
                          : isCandidate
                            ? "bg-red-100 text-red-900"
                            : "text-foreground"
                      }`}
                    >
                      <span className="font-mono text-xs uppercase text-muted-foreground">
                        {opt.id}.
                      </span>{" "}
                      {opt.text}
                    </li>
                  );
                })}
              </ul>
              <p className="mt-2 text-sm text-foreground">
                <span className="font-semibold">Why:</span>{" "}
                {
                  item.options.find(
                    (opt) => opt.id === item.correct_option_id,
                  )?.rationale
                }
              </p>
            </li>
          ))}
        </ol>
      </details>
    </section>
  );
}
